import { createFileRoute } from '@tanstack/react-router'
import { Home } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { CustomerDetailTabs } from '@/features/customers/components/CustomerDetailTabs'
import {
  type CustomerResponse,
  customerService,
} from '@/features/customers/services/customerService'
import { useEnvironment } from '@/features/environments/contexts/EnvironmentContext'
import { Badge } from '@/shared/components/ui/badge'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { useBreadcrumbs } from '@/shared/hooks/useBreadcrumbs'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'

export const Route = createFileRoute('/_auth/customers/$customerId/')({
  component: CustomerDetailPage,
})

function CustomerDetailPage() {
  const { customerId } = Route.useParams()
  const { currentEnvironment } = useEnvironment()
  const { setBreadcrumbs } = useBreadcrumbs()
  const [customer, setCustomer] = useState<CustomerResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-fetch when environment changes
  useEffect(() => {
    const loadCustomer = async () => {
      setLoading(true)
      try {
        // Fetch customer by ID from API including organization details
        const foundCustomer = await customerService.getCustomerById(customerId)
        setCustomer(foundCustomer)
      } catch (err) {
        toast.error(extractErrorMessage(err))
        setError('Failed to load customer')
      } finally {
        setLoading(false)
      }
    }

    loadCustomer()
  }, [customerId, currentEnvironment?.id])

  const breadcrumbsContent = useMemo(() => {
    if (!customer) return null

    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to='/dashboard' className='flex items-center gap-1'>
              <Home className='w-4 h-4' />
              Dashboard
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink to='/customers'>Customers</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{customer.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    )
  }, [customer])

  useEffect(() => {
    if (breadcrumbsContent) {
      setBreadcrumbs(breadcrumbsContent)
    }
  }, [setBreadcrumbs, breadcrumbsContent])

  const getStatusVariant = (customer: CustomerResponse) => {
    switch (customer.status) {
      case 'active':
        return 'default'
      case 'inactive':
        return 'secondary'
      case 'suspended':
        return 'destructive'
      case 'archived':
        return 'outline'
      default:
        return 'secondary'
    }
  }

  const getStatusLabel = (customer: CustomerResponse) => {
    switch (customer.status) {
      case 'active':
        return 'Active'
      case 'inactive':
        return 'Inactive'
      case 'suspended':
        return 'Suspended'
      case 'archived':
        return 'Archived'
      default:
        return customer.status
    }
  }

  if (loading) {
    return (
      <div className='space-y-6'>
        <div className='text-center py-12'>
          <div className='text-muted-foreground'>
            Loading customer details...
          </div>
        </div>
      </div>
    )
  }

  if (error || !customer) {
    return (
      <div className='space-y-6'>
        <div className='text-center py-12'>
          <div className='text-red-600 mb-4'>
            {error || 'Customer not found'}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center space-x-4'>
          <div className='w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-lg font-medium'>
            {customer.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 className='text-lg font-bold'>{customer.name}</h1>
            <div className='flex items-center gap-3 mt-1'>
              <p className='text-lg text-muted-foreground'>
                {customer.email || 'No email'}
              </p>
              <Badge variant={getStatusVariant(customer)}>
                {getStatusLabel(customer)}
              </Badge>
              {customer.type === 'agent' && (
                <Badge variant='outline'>Agent</Badge>
              )}
            </div>
          </div>
        </div>

        <div className='flex items-center gap-3'>
          <div className='text-right text-sm text-muted-foreground'>
            <div>Customer ID: {customer.id}</div>
            {customer.external_id && (
              <div>External ID: {customer.external_id}</div>
            )}
          </div>
        </div>
      </div>

      {/* Customer Detail Tabs */}
      <CustomerDetailTabs customer={customer} onCustomerUpdated={setCustomer} />
    </div>
  )
}
